import { X } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { BundleNote } from "@/components/kit/BundleNote";
import { RecipeSkeleton } from "@/components/kit/Skeleton";
import { KIT } from "@/constants/testIds";
import { formatDuration, nutritionRows } from "@/lib/nutrition";

const Meta = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.filter((i) => i.value).map((i) => (
      <span key={i.label} className="rounded-full bg-secondary px-3.5 py-1.5 text-xs text-brand-tealSoft">
        {i.label} <strong className="font-bold text-brand-teal">{i.value}</strong>
      </span>
    ))}
  </div>
);

export const RecipeModal = ({ open, onOpenChange, recipe, proteinLabel, loading = false }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid={KIT.recipeModal}
      className="max-h-[92vh] max-w-[600px] gap-0 overflow-y-auto rounded-[24px] border-border bg-card p-0 shadow-hero"
    >
      <DialogClose
        data-testid={KIT.recipeModalClose}
        className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-brand-teal shadow-card backdrop-blur transition-colors hover:bg-white"
        aria-label="Close recipe"
      >
        <X className="h-4 w-4" />
      </DialogClose>

      {loading || !recipe ? (
        <>
          <DialogTitle className="sr-only">Loading recipe</DialogTitle>
          <DialogDescription className="sr-only">Fetching the full recipe.</DialogDescription>
          <RecipeSkeleton />
        </>
      ) : (
        <>
          {recipe.image && (
            <img src={recipe.image} alt="" className="aspect-square w-full object-cover" />
          )}
          <div className="space-y-7 p-7 sm:p-8">
            <div className="space-y-3">
              <DialogTitle className="font-display text-[26px] font-semibold leading-tight text-brand-teal sm:text-3xl">
                {recipe.title}
              </DialogTitle>
              <DialogDescription
                className={
                  recipe.summary ? "text-[15px] leading-relaxed text-muted-foreground" : "sr-only"
                }
              >
                {recipe.summary ?? `Ingredients and method for ${recipe.title}.`}
              </DialogDescription>
              <Meta
                items={[
                  { label: "Serves", value: recipe.servings },
                  { label: "Prep", value: formatDuration(recipe.prep_time) },
                  { label: "Cook", value: formatDuration(recipe.cook_time) },
                ]}
              />
            </div>

            {recipe.pending && (
              <p
                data-testid={KIT.recipePending}
                className="rounded-[18px] border border-brand-teal/12 bg-brand-teal/[0.035] p-5 text-[15px] leading-relaxed text-brand-teal"
              >
                The full method and per-serving nutrition for this dish are published on Plattershare
                and will appear here as soon as the recipe goes live.
              </p>
            )}

            {/* Guidance for the dish actually opened — this is where a chosen sabzi answers for itself. */}
            {recipe.protein_suggestion && (
              <BundleNote kind="protein" label={proteinLabel} testId="bundle-note-protein-modal" text={recipe.protein_suggestion} />
            )}

            {!!recipe.ingredients?.length && (
              <section className="space-y-3">
                <h4 className="ps-eyebrow">Ingredients</h4>
                <ul className="divide-y divide-border">
                  {recipe.ingredients.map((ing, i) => (
                    <li key={i} className="flex justify-between gap-4 py-3 text-[15px] text-brand-teal">
                      <span>
                        {ing.name}
                        {ing.notes ? `, ${ing.notes}` : ""}
                      </span>
                      <span className="shrink-0 font-medium text-muted-foreground">
                        {[ing.amount, ing.unit].filter(Boolean).join(" ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {!!recipe.steps?.length && (              <section className="space-y-3">
                <h4 className="ps-eyebrow">Method</h4>
                <ol className="space-y-4">
                  {recipe.steps.map((step, i) => (
                    <li key={i} className="flex gap-4 text-[15px] leading-relaxed text-brand-teal">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-teal font-display text-xs font-bold text-brand-cream">
                        {i + 1}
                      </span>
                      <p>{step}</p>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {!!nutritionRows(recipe.nutrition).length && (
              <section className="space-y-3">
                <h4 className="ps-eyebrow">Per serving</h4>
                <dl className="grid gap-x-6 sm:grid-cols-2">
                  {nutritionRows(recipe.nutrition).map((row) => (
                    <div
                      key={row.key}
                      data-testid={KIT.nutritionRow(row.key)}
                      className="flex items-baseline justify-between gap-4 border-b border-border py-2.5 text-[14.5px]"
                    >
                      <dt className="text-brand-teal/65">{row.label}</dt>
                      <dd className="font-semibold text-brand-teal">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* Authored per-recipe guidance. Only the Kids Tiffin plan ships these, so the section
                simply does not exist for other plans. */}
            {!!recipe.notes?.length && (
              <section data-testid="recipe-notes" className="space-y-3">
                <h4 className="ps-eyebrow">Tiffin Notes &amp; Tips</h4>
                <p className="text-[13.5px] text-brand-teal/60">for {recipe.title}</p>
                <div className="grid gap-2.5">
                  {recipe.notes.map((note) => (
                    <div
                      key={note.label}
                      data-testid={`recipe-note-${note.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-[18px] border border-brand-teal/10 bg-brand-teal/[0.035] p-4"
                    >
                      <p className="font-display text-[15px] font-semibold text-brand-teal">{note.label}</p>
                      <p className="mt-1 text-[14.5px] leading-relaxed text-brand-teal/75">{note.text}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
